import React, {useCallback, useEffect, useState} from "react";
import {useApolloClient, useMutation, useQuery} from "@apollo/client";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {useLocalStorage, VisuallyHidden} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";
import {useSocketListener} from "@unibuddy/sockets";

import PageHeader from "shared/Shared/components/PageHeader/PageHeader";
import {setMe} from "shared/Auth/actions/authActions";
import {rootURL} from "shared/Utils/WhereAmI";
import {ErrorDisplay} from "shared/General/components/ErrorDisplay/ErrorDisplay";
import {PhoneNumberToast} from "shared/Chat/components/PhoneNumberToast/PhoneNumberToast";
import checkAllowDelinkedForApplicant from "shared/Chat/utils/checkAllowDelinkedForApplicant";
import Queue, {QueueItem} from "shared/General/components/Queue/Queue";
import {
    pageNames,
    popupNames,
    sharedEvents,
} from "shared/AnalyticsNew/constants/productSpecification";

import {PageTitle} from "../../../../../components/PageTitle/PageTitle";
import DelinkedMessage from "../../components/DelinkedMessageBox/DelinkedMessage";
import enableSMSMutation from "../../mutations/enableSMSMutation";
import LoadingChat from "../../components/LoadingChat/LoadingChat";
import s from "./Chat.pcss";
import {StyledChatComponent} from "../common/StyledChatComponent";
import {ChatUI} from "../../components/ChatUI/ChatUI";
import {PopupQuestionWrapper} from "../../components/PopupQuestions/PopupQuestionWrapper";
import {IntlNamespaces} from "../../../../../translations/types";
import {
    INTL_ADD_PHONE_NUMBER_MESSAGE,
    INTL_CONVERSATION_WITH,
    INTL_NOTIFICATION_MESSAGE,
    INTL_REPLY_LATER,
} from "../../../../../translations/chat";
import {isFunction} from "../../../../../util/isFunction";
import {INTL_INBOX} from "../../../../../translations/inbox";
import {INTL_BACK} from "../../../../../translations/common";
import useAnalytics from "../../../../../shared/AnalyticsNew/hooks/useAnalytics";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import useAuth from "../../../../../shared/Auth/hooks/useAuth";
import {useEmbeddedEvents} from "../../hooks/useEmbeddedEvents";
import sharedEmbeddedEvents from "../../../../../shared/EmbeddedEvents/constants/EmbeddedEvents";
import useErrorReporting from "../../../../../shared/General/components/ErrorReporting/useErrorReporting";
import {useHasUniversityAppliedStatus} from "../../hooks/useHasUniversityAppliedStatus";
import {PopupQuestions} from "../../components/PopupQuestions/PopupQuestions";
import recordActivityMutation from "../../../../../shared/Activity/mutations/recordActivityMutation";
import {getReduxDispatch} from "../../../../../App";
import {sendMessageEvent} from "../../../SocketWrapperProvider/Sockets";
import {writeConversationMessageToApolloCache} from "../../../../../shared/Chat/functions/writeConversationMessageToApolloCache";
import {
    ChatMessage,
    GetChatGroupByIdQueryQuery,
    GetChatGroupByIdQueryQueryVariables,
    MessageField,
} from "../../../../../types/generated";
import {useChat} from "../../providers/ChatProvider";
import {sendConversationMessageMutation} from "../../mutations/sendConversationMessageMutation";
import {getChatConversationMessagesQuery} from "../../queries/getChatConversationMessagesQuery";
import {GetChatGroupIdQuery} from "../../queries/getChatGroupByIdQuery";
import {optimisticSendConversationMessageResponse} from "../../mutations/optimisticSendConversationResponse";

const CHAT_CONVERSATION_MESSAGES_LIMIT = 1000;

export function Chat() {
    const {trackEvent} = useAnalytics();
    const history = useHistory();
    const {authState} = useAuth();
    const postEmbeddedMessage = useEmbeddedEvents();
    const {conversationId} = useParams<{conversationId: string}>();
    const {chatGroupId} = useChat();
    const university = useUniversity();
    const [savePhoneNumber] = useMutation(enableSMSMutation);
    const [sendMessage] = useMutation(sendConversationMessageMutation);
    const [recordActivity] = useMutation(recordActivityMutation);
    const client = useApolloClient();
    const dispatch = getReduxDispatch();

    const [phonePopupDismissed, setPhonePopupDismissed] = useLocalStorage(
        `PHONE_POPUP_DISMISSED_${chatGroupId}`,
        false,
    );

    const [chatServicePhonePopupDismissed, setChatServicePhonePopupDismissed] = useLocalStorage(
        `CHAT_SERVICE_PHONE_POPUP_DISMISSED_${conversationId}`,
        false,
    );
    const [updatingPhoneNumber, setUpdatingPhoneNumber] = useState<boolean>(false);
    const {slug: universitySlug, features} = university;
    const isDelinked = checkAllowDelinkedForApplicant(universitySlug, authState?.me);
    const {
        data: messagesData,
        loading: messagesLoading,
        error: messagesError,
    } = useQuery(getChatConversationMessagesQuery, {
        variables: {
            getMessageDto: {
                conversationId,
                limit: CHAT_CONVERSATION_MESSAGES_LIMIT,
            },
        },
        fetchPolicy: "cache-and-network",
    });

    const {
        data: chatGroupData,
        loading: chatGroupLoading,
        error: chatGroupError,
    } = useQuery<GetChatGroupByIdQueryQuery, GetChatGroupByIdQueryQueryVariables>(
        GetChatGroupIdQuery,
        {
            variables: {chatGroupId},
            skip: !chatGroupId,
        },
    );

    const messages = messagesData?.getChatConversationMessages?.messages ?? [];
    const {opponent = {}} = chatGroupData?.chatGroupById ?? {};
    const {firstName: opponentName = "Buddy", id: opponentId = "", accountRole} = opponent;
    let opponentType = accountRole === "bot" ? "university" : accountRole ?? "";
    const {pathname, search} = useLocation();
    const {reportError} = useErrorReporting();
    const {t} = useTranslation([IntlNamespaces.CHAT, IntlNamespaces.INBOX, IntlNamespaces.COMMON]);
    const showAppliedToUniversityQuestion = !features.get("hideAppliedToUniversityQuestion");
    const hasUniversityAppliedStatus = useHasUniversityAppliedStatus();
    const showAddPhoneNumber = authState?.me?.applicant?.phoneNumber === null;
    const phoneNumberText = showAddPhoneNumber
        ? t(INTL_ADD_PHONE_NUMBER_MESSAGE.key, INTL_ADD_PHONE_NUMBER_MESSAGE.defaultValue)
        : t(INTL_NOTIFICATION_MESSAGE.key, INTL_NOTIFICATION_MESSAGE.defaultValue);

    const onSend = async ({text}: {text: string}) => {
        if (text === "") return;

        const cacheVariables = {
            getMessageDto: {
                conversationId,
                limit: CHAT_CONVERSATION_MESSAGES_LIMIT,
            },
        };

        const variables = {
            messageDto: {
                text,
                conversationId,
            },
        };

        const optimisticResponseObject = optimisticSendConversationMessageResponse({
            text,
            created: new Date().toISOString(),
            sender: authState?.me?.anyUser,
        });

        const optimisticMessage = optimisticResponseObject?.sendConversationMessage;

        try {
            const {getChatConversationMessages: messagesCache} = client.readQuery({
                query: getChatConversationMessagesQuery,
                variables: cacheVariables,
            });
            if (messagesCache) {
                client.writeQuery({
                    query: getChatConversationMessagesQuery,
                    variables: cacheVariables,
                    data: {
                        getChatConversationMessages: {
                            messages: [...messagesCache.messages, optimisticMessage],
                            hasMore: messagesCache.hasMore,
                            __typename: "PaginatedChatMessages",
                        },
                    },
                });
            }

            const {data} = await sendMessage({variables});

            if (data?.sendConversationMessage) {
                const {getChatConversationMessages: updatedMessagesCache} = client.readQuery({
                    query: getChatConversationMessagesQuery,
                    variables: cacheVariables,
                });
                if (updatedMessagesCache) {
                    const newMessages = [
                        ...updatedMessagesCache.messages.filter(
                            (m: ChatMessage) => !m.id.startsWith("optimistic-"),
                        ),
                        data.sendConversationMessage,
                    ];
                    client.writeQuery({
                        query: getChatConversationMessagesQuery,
                        variables: cacheVariables,
                        data: {
                            getChatConversationMessages: {
                                messages: newMessages,
                                hasMore: updatedMessagesCache?.hasMore ?? false,
                                __typename: "PaginatedChatMessages",
                            },
                        },
                    });
                }
            }

            if (opponentType === "mentor" && opponent?.degreeLevel === "Alumni") {
                opponentType = "alumni";
            }

            trackEvent(sharedEvents.messageSent.name, {
                conversationId: conversationId ?? "",
                opponentId,
                opponentType,
                chatVersion: "Version 2",
            });

            const isEmbedded = window.parent !== window;

            try {
                if (isEmbedded) {
                    postEmbeddedMessage(sharedEmbeddedEvents.messageSent.name, {});
                }
            } catch (error) {
                console.error("Error sending embedded message:", error);
            }
        } catch (error) {
            reportError(error);
        }
    };

    const onAmbassadorNewMessage = useCallback(
        (message: ChatMessage) => {
            const user = authState?.me?.anyUser;
            if (user && message?.sender?.id === user.id) return;

            try {
                const cacheVariables = {
                    getMessageDto: {
                        conversationId,
                        limit: CHAT_CONVERSATION_MESSAGES_LIMIT,
                    },
                };
                writeConversationMessageToApolloCache(message, client, cacheVariables);
            } catch (e) {
                console.error("Error writing message to cache ", e);
            }
        },
        [authState, client, conversationId],
    );

    useSocketListener({
        event: sendMessageEvent,
        channelId: conversationId,
        onEvent: onAmbassadorNewMessage,
    });

    const onAddPhoneNumber = async (phoneNumber: string, service: string) => {
        setUpdatingPhoneNumber(true);
        try {
            const {data} = await savePhoneNumber({
                variables: {
                    phoneNumber,
                    chatGroupId,
                    applicant: authState?.me?.anyUser?.id,
                    mobileAdded: true,
                    newMessageNotifications: ["email", service],
                },
            });

            if (data?.updateApplicant?.error) {
                setUpdatingPhoneNumber(false);
                reportError(data?.updateApplicant?.error);
                return false;
            }
            dispatch(setMe(data?.updateApplicant?.me));
            dispatch(setMe(data?.updateApplicantPreferences?.me));
            setUpdatingPhoneNumber(false);
            return true;
        } catch (error) {
            reportError(error);
            return false;
        }
    };

    const shouldShowWillReply = () => {
        // only display this if the opponent is a mentor
        if (opponentType === "mentor") {
            // do not show reply scenarios:
            // 1. Ambassador/uni replied
            // 2. Dismissed
            // 3. Phone number added
            const replyReceived =
                messages.filter(
                    (message: MessageField) => message?.sender?.accountRole !== "applicant",
                ).length > 1;
            const mobileAdded = authState?.me?.applicant?.phoneNumber;
            return (
                !replyReceived &&
                !phonePopupDismissed &&
                !chatServicePhonePopupDismissed &&
                !mobileAdded
            );
        }
        return false;
    };

    const errorDisplayBackFunction = () => {
        trackEvent(sharedEvents.clickedBackInChat.name, {
            universitySlug,
        });
        history.push(`${rootURL(pathname)}/inbox${search}`);
    };

    const redirectToRelink = () => {
        // redirecting to 2nd stage signup on settings tab
        history.replace(pathname, {from: "delinked"});
    };

    const queue = (
        <Queue>
            {showAppliedToUniversityQuestion && (
                <QueueItem enabled={!hasUniversityAppliedStatus}>
                    {({isOpen, onDismiss}) => {
                        return <PopupQuestions onDismiss={onDismiss} isOpen={isOpen} />;
                    }}
                </QueueItem>
            )}
            <QueueItem enabled={shouldShowWillReply()}>
                {({isOpen, onDismiss}) => {
                    const onDismissModal = () => {
                        if (showAddPhoneNumber) {
                            trackEvent(sharedEvents.phoneNumberCollectionDismissed.name, {
                                universitySlug,
                                popup: popupNames.phoneNumberCollection,
                                cta: "Close Button",
                            });
                        }
                        onDismiss();
                        setPhonePopupDismissed(true);
                        setChatServicePhonePopupDismissed(true);
                    };
                    return (
                        <PopupQuestionWrapper onDismiss={onDismissModal} isOpen={isOpen}>
                            <PhoneNumberToast
                                isOpen={isOpen}
                                title={t(
                                    INTL_REPLY_LATER.key,
                                    isFunction(INTL_REPLY_LATER.defaultValue)
                                        ? INTL_REPLY_LATER.defaultValue(name)
                                        : INTL_REPLY_LATER.defaultValue,

                                    {
                                        name: opponentName,
                                    },
                                )}
                                text={phoneNumberText}
                                onAddPhoneNumber={onAddPhoneNumber}
                                updatingPhoneNumber={updatingPhoneNumber}
                                showAddPhoneNumber={showAddPhoneNumber}
                            />
                        </PopupQuestionWrapper>
                    );
                }}
            </QueueItem>
        </Queue>
    );

    useEffect(() => {
        trackEvent(sharedEvents.chatPageViewed.name, {prefix: pageNames.chat, universitySlug});
        // Empty deps array as we only want this to fire once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const recordActivityFunction = async () => {
            try {
                await recordActivity({
                    variables: {
                        app: "onix",
                        activity: "Chat",
                        activitySource: JSON.stringify({slug: universitySlug, type: "university"}),
                    },
                });
            } catch (error) {
                reportError(error);
            }
        };

        recordActivityFunction();

        // Empty deps array as we only want this to fire once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (messages?.length < 1 || messagesLoading || chatGroupLoading) {
        return (
            <div id="chat-page">
                <PageTitle
                    title={t(
                        `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                        isFunction(INTL_CONVERSATION_WITH.defaultValue)
                            ? INTL_CONVERSATION_WITH.defaultValue(name)
                            : INTL_CONVERSATION_WITH.defaultValue,
                        {name: opponentName},
                    )}
                />
                <VisuallyHidden>
                    <PageHeader
                        title={t(
                            `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                            INTL_INBOX.defaultValue,
                        )}
                    />
                </VisuallyHidden>
                <div className={s.container}>
                    <StyledChatComponent className={s.chat}>
                        <LoadingChat />
                    </StyledChatComponent>
                </div>
            </div>
        );
    }

    return (
        <ErrorDisplay
            // TODO: Do we want to show error display when sending a message fails? If not, how should we handle it?
            error={messagesError || chatGroupError}
            actionButton={{
                text: t(`${IntlNamespaces.COMMON}:${INTL_BACK.key}`, INTL_BACK.defaultValue),
                onClick: () => errorDisplayBackFunction(),
            }}
        >
            <div id="chat-page">
                <PageTitle
                    title={t(
                        `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                        isFunction(INTL_CONVERSATION_WITH.defaultValue)
                            ? INTL_CONVERSATION_WITH.defaultValue(name)
                            : INTL_CONVERSATION_WITH.defaultValue,
                        {name: opponentName},
                    )}
                />
                <VisuallyHidden>
                    <PageHeader
                        title={t(
                            `${IntlNamespaces.INBOX}:${INTL_INBOX.key}`,
                            INTL_INBOX.defaultValue,
                        )}
                    />
                </VisuallyHidden>
                <div className={s.container}>
                    <StyledChatComponent className={s.chat}>
                        <ChatUI
                            messages={messages}
                            user={authState?.me?.anyUser}
                            chatFooterHidden={isDelinked}
                            chatPopup={
                                isDelinked ? (
                                    <DelinkedMessage redirectToRelink={redirectToRelink} />
                                ) : null
                            }
                            university={university}
                            onSend={onSend}
                            queue={queue}
                        />
                    </StyledChatComponent>
                </div>
            </div>
        </ErrorDisplay>
    );
}
